import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchPage } from "@/lib/api";
import Seo from "@/components/public/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useI18n } from "@/i18n";
import { withSiteName } from "@/lib/seo";

export default function FaqPage({ initialPage }) {
  const { t } = useI18n();
  const [page, setPage] = useState(initialPage || null);
  const [loading, setLoading] = useState(!initialPage);
  const [loadError, setLoadError] = useState(initialPage?.__ssrError === true && initialPage.status !== 404);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (initialPage) return undefined;
    let active = true;
    setLoading(true);
    setLoadError(false);
    fetchPage("faq")
      .then((result) => { if (active) setPage(result); })
      .catch(() => {
        if (active) {
          setPage(null);
          setLoadError(true);
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // SSR props are initial-only; later route mounts receive no SSR payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  if (loading) {
    return (
      <div className="flex justify-center py-20" role="status" aria-live="polite" aria-label="جارٍ تحميل الأسئلة الشائعة">
        <Loader2 className="w-7 h-7 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">جارٍ تحميل الأسئلة الشائعة…</span>
      </div>
    );
  }

  const items = (Array.isArray(page?.data) ? page.data : []).filter((item) =>
    typeof item?.q === "string" && item.q.trim() &&
    typeof item?.a === "string" && item.a.trim()
  );
  const jsonLd = items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  } : undefined;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-12 pb-6">
      <Seo
        title={withSiteName(page?.meta_title || page?.title || t("faq"))}
        description={page?.meta_description}
        path="/faq"
        jsonLd={jsonLd}
        noIndex={loadError || items.length === 0}
      />
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">
        {page?.title || t("faq")}
      </h1>
      {loadError ? (
        <div role="alert" className="aran-card p-5 text-center">
          <p className="text-sm text-muted-foreground">تعذّر تحميل الأسئلة الشائعة حالياً.</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)} className="mt-4 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            إعادة المحاولة
          </button>
        </div>
      ) : items.length > 0 ? (
        <div className="aran-card p-3 sm:p-5">
          <Accordion type="single" collapsible className="w-full">
            {items.map((it, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-right font-semibold hover:no-underline text-base">
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-loose text-sm">
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ) : (
        <div role="status" className="aran-card p-5 text-center text-sm text-muted-foreground">
          لا توجد أسئلة شائعة منشورة حالياً.
        </div>
      )}
    </div>
  );
}
